// --- Dependencies -------------------------------------------------------------------------------
import { create } from "zustand";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import type { TyFile } from "@/types";
import { makeThumbnail } from "@/utils";
import { callApi, putToStorage } from "./transport";
import type { TyUploadItem, TyUploadOwner, TyUploadsStore } from "./types";
// --- Logic --------------------------------------------------------------------------------------
type TyReserved = { file: TyFile; uploadUrl: string; thumbUploadUrl: string | null };

/** Live XHR handles, kept outside the store — they are not state and must never trigger a render. */
const running = new Map<string, () => void>();

/**
 * Every upload in the app, in one place that outlives every screen.
 *
 * Uploads used to block the form that started them: the person saved a project and then watched a
 * spinner while the pictures crawled through our server. Now the record is saved immediately and the
 * bytes go straight to S3 from here, so they can walk away, open another section, and watch the tray
 * out of the corner of their eye.
 *
 * The store is deliberately **not persisted**. A `File` handle cannot survive a page reload, so a
 * queue restored from storage would be a list of uploads that can never finish.
 */
const useUploads = create<TyUploadsStore>()((set, get) => ({
    items: [],
    isOpen: true,
    /** Bumped whenever an upload finishes, so whatever is on screen can reload itself. */
    finishedAt: null,

    setOpen: (isOpen) => set({ isOpen }),

    clearFinished: () => set((state) => ({ items: state.items.filter((item) => item.status !== "done" && item.status !== "cancelled") })),

    patch: (id, changes) => set((state) => ({ items: state.items.map((item) => (item.id === id ? { ...item, ...changes } : item)) })),

    /**
     * One `reserve` for the whole batch, because the quota is checked against the batch — sending them
     * one by one would let a selection half-land and leave someone guessing which files made it.
     */
    enqueue: async (files, owner) => {
        if (!files.length) return;

        // Shown before the server has answered: a person who just picked eleven files wants to see
        // eleven rows, not a pause.
        const staged: TyUploadItem[] = files.map((file, index) => ({
            id: `staging-${Date.now()}-${index}`,
            name: file.name,
            size: file.size,
            progress: 0,
            status: "waiting",
            error: null,
            file,
            owner,
        }));
        set((state) => ({ items: [...staged, ...state.items], isOpen: true }));

        let reserved: TyReserved[];
        try {
            const data = await callApi<{ reserved: TyReserved[] }>("/drive/reserve", "POST", {
                files: files.map((file) => ({ name: file.name, size: file.size, mimeType: file.type })),
                ...(owner?.model && owner.id ? { ownerModel: owner.model, ownerId: owner.id } : {}),
                ...(owner?.slot ? { slot: owner.slot } : {}),
                ...(owner?.name ? { name: owner.name } : {}),
                ...(owner?.tags?.length ? { tags: owner.tags } : {}),
            });
            reserved = data.reserved;
        } catch (err) {
            const message = (err as Error).message;
            return set((state) => ({
                items: state.items.map((item) => (staged.some((row) => row.id === item.id) ? { ...item, status: "failed", error: message } : item)),
            }));
        }

        // The staged rows take on their real ids, which is what cancel and confirm address.
        set((state) => ({
            items: state.items.map((item) => {
                const at = staged.findIndex((row) => row.id === item.id);
                if (at < 0 || !reserved[at]) return item;
                return { ...item, id: String(reserved[at].file._id), uploadUrl: reserved[at].uploadUrl, thumbUploadUrl: reserved[at].thumbUploadUrl };
            }),
        }));

        get().pump();
    },

    /**
     * A replacement is the same journey with a different first step: the row already exists, so the
     * server hands back a URL for a *new* key and keeps serving the old object until `confirm` swaps
     * them. Nothing disappears because an upload failed halfway.
     */
    enqueueReplace: async (fileId, file) => {
        const staged: TyUploadItem = { id: fileId, name: file.name, size: file.size, progress: 0, status: "waiting", error: null, file };
        set((state) => ({ items: [staged, ...state.items.filter((item) => item.id !== fileId)], isOpen: true }));

        try {
            const data = await callApi<TyReserved>(`/drive/${fileId}/replace`, "POST", { name: file.name, mimeType: file.type });
            get().patch(fileId, { uploadUrl: data.uploadUrl, thumbUploadUrl: data.thumbUploadUrl });
        } catch (err) {
            return get().patch(fileId, { status: "failed", error: (err as Error).message });
        }

        get().pump();
    },

    /** Starts as many waiting files as the concurrency allows, and is re-entered as each one lands. */
    pump: () => {
        const items = get().items;
        const active = items.filter((item) => item.status === "uploading").length;
        const free = constants.uploadConcurrency - active;
        if (free <= 0) return;

        items
            .filter((item) => item.status === "waiting" && item.uploadUrl)
            .slice(0, free)
            .forEach((item) => get().send(item.id));
    },

    send: async (id) => {
        const item = get().items.find((row) => row.id === id);
        if (!item?.file || !item.uploadUrl) return;

        get().patch(id, { status: "uploading", progress: 0, error: null });

        try {
            // Drawn before the upload starts, so a failure here costs nothing and the file still goes.
            const thumbnail = item.thumbUploadUrl ? await makeThumbnail(item.file) : null;

            const handle = putToStorage(item.uploadUrl, item.file, (progress) => get().patch(id, { progress }));
            running.set(id, handle.abort);
            await handle.done;
            running.delete(id);

            if (thumbnail && item.thumbUploadUrl) await putToStorage(item.thumbUploadUrl, thumbnail).done;

            // Only now is it a file: the server checks the object really landed and reads its real size.
            await callApi(`/drive/${id}/confirm`, "POST", { hasThumb: !!thumbnail });

            get().patch(id, { status: "done", progress: 100, file: undefined });
            set({ finishedAt: Date.now() });
        } catch (err) {
            running.delete(id);
            const message = (err as Error).message;
            // An abort is the person's own doing, not a failure to report back at them.
            if (message !== "aborted") get().patch(id, { status: "failed", error: message });
            else set({ finishedAt: Date.now() });
        }

        get().pump();
    },

    cancel: async (id) => {
        running.get(id)?.();
        running.delete(id);
        get().patch(id, { status: "cancelled", file: undefined });

        // The row exists before the bytes do, so calling it off has to clear the row as well. A row
        // left behind would be swept by the reconcile job anyway, but not before it counted as pending.
        try {
            if (!id.startsWith("staging-")) await callApi(`/drive/${id}`, "DELETE");
        } catch {
            /* the reconcile job is the backstop */
        }

        set({ finishedAt: Date.now() });
        get().pump();
    },

    retry: (id) => {
        const item = get().items.find((row) => row.id === id);
        if (!item?.file || !item.uploadUrl) return;
        get().patch(id, { status: "waiting", progress: 0, error: null });
        get().pump();
    },
}));

export default useUploads;
export type { TyUploadItem, TyUploadOwner };
